import { IGameState } from 'State/Game/IGameState';
import { IPlayerExpireableQuestData } from 'State/Player/IPlayerState';
import { ArrayHelper } from 'Utility/ArrayHelper';
import { EventType } from 'Utility/Helpers/Events/EventType';
import { IEventStateData } from './IEventsState';
import { EventLayoutMap } from 'Constants/Events/EventLayoutMap';
import DateHelper from 'Utility/DateHelper';
import { getHighestLevelCompleted } from 'State/Player/PlayerLevelHelper';
import { isUndefined, isNumber } from 'Utility/Types';
export namespace EventStateHelper {
    export interface IGetEventsOptions {
        isLoaded?: boolean;
        isActive?: boolean;
        hasFTUX?: boolean;
        isUnlocked?: boolean;
    }

    /**
     * Get events the have started before 'now' and will complete after 'now'. 'now' is determined using the Datehelper
     * and will also take into account server time offsets.
     * @param state
     */
    export function getActiveEvents(state: IGameState) {
        return state.eventsState.events.filter(event => {
            return isEventActive(state, event.type);
        });
    }

    /**
     * Gets events based off provided options
     * @param state
     * @param options - isActive defaults to true
     */
    export function getEvents(state: IGameState, options?: IGetEventsOptions) {
        const { isLoaded = false, isActive = true, hasFTUX = false, isUnlocked = false } = options ?? {};

        return state.eventsState.events.filter(event => {
            if (
                // If event is not loaded and we want only loaded events, then filter out
                (isLoaded && !event.isLoaded) ||
                // If event does not have an ftux and we want only events with one, then filter out
                (hasFTUX && !event.hasFTUX) ||
                // If player has not reached the required level to unlock the event, then filter out (if no required level is provided then the event is considered unlocked)
                (isUnlocked &&
                    isNumber(event.requiredLevel) &&
                    event.requiredLevel > getHighestLevelCompleted(state.playerState)) ||
                // If event is not active and we want only active events, then filter out
                (isActive && !isEventActive(state, event.type))
            )
                return false;

            // If still here then keep event
            return true;
        });
    }

    /**
     * Checks if the event has started before 'now' and will complete after 'now'. 'now' is determined using the Datehelper
     * and will also take into account server time offsets.
     * @param state
     */
    export function isEventActive(state: IGameState, eventType: EventType): boolean {
        const now = DateHelper.now;
        const eventState = getEventState(state, eventType);

        const startDate = DateHelper.getDate(eventState.startTime);
        const isBeforeDurationEnds = DateHelper.timeSince(startDate) <= eventState.duration;
        const isAfterEventStarts = DateHelper.getTimeUntil(now, startDate) <= 0;
        const meetsLvlRequirement = isUndefined(eventState.requiredLevel)
            ? true
            : getHighestLevelCompleted(state.playerState) >= eventState.requiredLevel;
        const notAtRequiredLevel = state.levelState.levelNumber !== eventState.requiredLevel;

        return isAfterEventStarts && isBeforeDurationEnds && meetsLvlRequirement && notAtRequiredLevel;
    }

    /**
     * Gets the questId for the event with the highest prio
     * @param state Game State
     * @returns QuestId of the event with the highest prio quest or undefined if none are found
     */
    export function getActiveQuestId(state: IGameState): string | undefined {
        return getActiveQuest(state)?.id;
    }

    export function getEventsWithQuests(state: IGameState): IEventStateData[] {
        return getActiveEvents(state).filter(event => event.hasQuests);
    }

    // TODO: Maybe we want to get events by id instead so that we can have multiple events of the same type active? Not sure of use case tho
    export function getEventState(state: IGameState, eventType: EventType): IEventStateData {
        return state.eventsState.events.find(event => event.type === eventType);
    }

    export function getActiveQuest(state: IGameState): IPlayerExpireableQuestData {
        // Active events with quests
        const eventsWithQuests = getEventsWithQuests(state);
        // Highest prio quest
        const eventWithActiveQuest = ArrayHelper.sortByHighestPriority(eventsWithQuests)[0];
        if (eventWithActiveQuest) {
            return state.playerState.eventQuests.find(quest => quest.eventType === eventWithActiveQuest.type);
        }
    }

    export function getEvent(state: IGameState, key: keyof typeof EventLayoutMap[EventType]) {
        return ArrayHelper.sortByHighestPriority(getActiveEvents(state)).find(
            event => EventLayoutMap[event.type]?.[key]
        );
    }

    export function getActiveEvent(state: IGameState): IEventStateData | undefined {
        return ArrayHelper.sortByHighestPriority(getActiveEvents(state))[0];
    }

    /**
     * TODO: Refactor so this is not required to determine which assets are required for each event/UI/scene etc.
     * @param event
     */
    export function isXmasEvent(event: IEventStateData) {
        return event?.type === EventType.XMAS_2021;
    }

    export function isEasterEvent(event: IEventStateData) {
        return event?.type === EventType.EASTER_2024;
    }

    export function isJul4Event(event: IEventStateData) {
        return event?.type === EventType.JUL4_2022;
    }

    export function isHalloweenEvent(event: IEventStateData) {
        return event?.type === EventType.HALLOWEEN_2022;
    }

    export function isXmas2023Event(event: IEventStateData) {
        return event?.type === EventType.XMAS_2023;
    }

    export function getEventLayout(state: IGameState, key: keyof typeof EventLayoutMap[EventType]) {
        const event = getEvent(state, key);

        if (!event) return null;

        if (EventLayoutMap[event.type] && EventLayoutMap[event.type][key]) {
            return new EventLayoutMap[event.type][key]();
        }

        return null;
    }
}
